import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProfileTemplate, ProfileTemplateService } from '@/services/profileTemplateService';
import { AlertCircle, ExternalLink, Loader2, Monitor, Smartphone, Tablet } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import type { ProfileData } from '@/apps/loan-officer-profile/hooks/useProfileData';
import {
  PROFILE_PREVIEW_MESSAGE_TYPE,
  PROFILE_PREVIEW_MESSAGE_VERSION,
  isProfilePreviewReadyMessage
} from '@/shared/profilePreviewContract';

interface Props {
  template: ProfileTemplate;
  previewProfile: Partial<ProfileData>;
  onClose: () => void;
  onSelect: () => void;
  selectionDisabled?: boolean;
  selectionDescriptionId?: string;
}

const VIEWPORTS = [
  { id: 'desktop', label: 'Desktop', width: '100%', icon: Monitor },
  { id: 'tablet', label: 'Tablet', width: '768px', icon: Tablet },
  { id: 'mobile', label: '390 px', width: '390px', icon: Smartphone },
  { id: 'small-mobile', label: '320 px', width: '320px', icon: Smartphone }
] as const;

export const TemplatePreviewModal: React.FC<Props> = ({
  template,
  previewProfile,
  onClose,
  onSelect,
  selectionDisabled = false,
  selectionDescriptionId
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [viewport, setViewport] = useState<(typeof VIEWPORTS)[number]['id']>('desktop');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewUrl = ProfileTemplateService.getPreviewUrl(template.id);
  const embeddedUrl = `${previewUrl}?embedded=1`;
  const selectedViewport = VIEWPORTS.find((item) => item.id === viewport) || VIEWPORTS[0];

  const sendDraft = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({
      type: PROFILE_PREVIEW_MESSAGE_TYPE,
      version: PROFILE_PREVIEW_MESSAGE_VERSION,
      templateId: template.id,
      profile: previewProfile
    }, window.location.origin);
  }, [previewProfile, template.id]);

  useEffect(() => {
    const receiveReady = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin
        || event.source !== iframeRef.current?.contentWindow
        || !isProfilePreviewReadyMessage(event.data, template.id)) {
        return;
      }
      sendDraft();
    };

    window.addEventListener('message', receiveReady);
    return () => window.removeEventListener('message', receiveReady);
  }, [sendDraft, template.id]);

  useEffect(() => {
    if (!isLoading) {
      sendDraft();
    }
  }, [isLoading, sendDraft]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[92vh] w-[96vw] max-w-[1500px] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <DialogTitle className="text-xl">{template.name} preview</DialogTitle>
              <DialogDescription className="mt-1">
                Live preview of the current draft. Missing fields use clearly labeled example content.
              </DialogDescription>
            </div>
            <div className="flex flex-wrap items-center gap-1 rounded-lg border bg-muted/40 p-1" aria-label="Preview viewport">
              {VIEWPORTS.map(({ id, label, icon: Icon }) => (
                <Button
                  key={id}
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setViewport(id)}
                  className={cn('min-h-9 gap-2', viewport === id && 'bg-background shadow-sm')}
                  aria-pressed={viewport === id}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{label}</span>
                </Button>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="relative flex min-h-0 flex-1 justify-center overflow-hidden bg-slate-200 p-3 sm:p-5">
          {isLoading && !hasError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/90">
              <div className="text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                <p className="mt-3 text-sm text-muted-foreground">Loading interactive preview…</p>
              </div>
            </div>
          )}

          {hasError ? (
            <div className="flex max-w-md items-center">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  The embedded preview could not load. Open it in a new tab or select the template and try again later.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div
              className="h-full max-w-full overflow-hidden rounded-lg border bg-white shadow-xl transition-[width] duration-200"
              style={{ width: selectedViewport.width }}
            >
              <iframe
                ref={iframeRef}
                src={embeddedUrl}
                className="h-full w-full border-0"
                title={`${template.name} interactive draft preview`}
                onLoad={() => {
                  setIsLoading(false);
                  sendDraft();
                }}
                onError={() => {
                  setIsLoading(false);
                  setHasError(true);
                }}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t bg-background px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <Button type="button" variant="outline" onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')} className="gap-2">
            <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open example in new tab
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="button"
              onClick={onSelect}
              disabled={selectionDisabled}
              aria-describedby={selectionDisabled ? selectionDescriptionId : undefined}
            >
              Select template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
